import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import md5 from 'md5';
import { Button, Card, Spinner } from 'react-bootstrap';
import api from '../../../services/api';
import { requestLaudoPresignDownload } from '../../../services/laudoFiles';
import history from '../../../services/history';
import { getPrefix } from '../../../util/utils';
import allowedToDelete from '../../../helpers/agenda/permissoes';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import useMapa from '../../../hooks/useMapa';

const isRequestAborted = err =>
  err?.name === 'AbortError' ||
  err?.name === 'CanceledError' ||
  err?.code === 'ERR_CANCELED';

export default function ReportMapa() {
  const { id } = useParams();
  const { data: mapa, error, isLoading } = useMapa(id);
  const laudoMapa = mapa?.[0];
  const { funcionario } = useSelector(state => state.funcionario);
  const medico = useSelector(state => state.doctor.profile);
  const [urls, setURLs] = useState([]);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    const codMapa = laudoMapa?.CODMAPA;
    if (!codMapa) return undefined;

    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchAndProcessFiles = async () => {
      try {
        const { data } = await api.get('/files', {
          params: { codLaudo: codMapa, procedimento: 'mapa' },
          signal,
        });

        if (signal.aborted) return;

        const pdfFiles = data.filter(item => item.Key.includes('.pdf'));
        setFiles(pdfFiles);

        const urlPromises = pdfFiles.map(file =>
          requestLaudoPresignDownload({ key: file.Key }, { signal })
        );

        const urlResults = await Promise.all(urlPromises);
        if (!signal.aborted) setURLs(urlResults);
      } catch (err) {
        if (isRequestAborted(err) || signal.aborted) return;

        toast.error(err?.response?.data?.error || 'Erro ao carregar!');
      }
    };

    fetchAndProcessFiles();

    return () => {
      abortController.abort();
      setFiles([]);
      setURLs([]);
    };
  }, [laudoMapa?.CODMAPA]);

  async function deleteFile(index = 0) {
    try {
      const res = await api.delete('/files', {
        data: { key: files[index].Key },
      });

      toast.success(res.data.message);
      setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
      setURLs(prevUrls => prevUrls.filter((_, i) => i !== index));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao deletar arquivo');
    }
  }

  return (
    <Card className="mt-2 mb-2">
      <LaudoCardHeader title="LAUDO DE MAPA" />

      {error && !isLoading && (
        <>
          <span className="text-danger text-center d-block" style={{ marginTop: 10 }}>
            Erro ao carregar laudo de mapa! Tente novamente.
          </span>
          <Card.Footer className="text-muted text-center">
            <Button variant="warning" onClick={() => history.goBack()}>
              Voltar
            </Button>
          </Card.Footer>
        </>
      )}

      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <Spinner animation="border" variant="dark" size="lg" />
        </div>
      )}

      {!isLoading && !error && laudoMapa && (
        <>
          <Card.Body>
            <strong className="mr-auto">Nome do Paciente: </strong>
            {`${laudoMapa.CODPAC} - ${laudoMapa.NOMEPAC}`}
            <br></br>
            <strong className="mr-auto">Data de Nasc: </strong>
            {`${laudoMapa.dataNasc}`}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Sexo: </strong>
            {`${laudoMapa.SEXOPAC}`}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Idade: </strong>
            {`${laudoMapa.idadePAC} anos`}
            <br></br>
            <strong className="mr-auto">Convênio: </strong>
            {laudoMapa.DESCRICONV}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Cod MAPA: </strong>
            {laudoMapa.CODMAPA}&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Data do Exame: </strong>
            {laudoMapa.dataMapa}
            {/* <br></br>
            <strong className="mr-auto">
              Médico que Realizou: {getPrefix(laudoMapa.CODREALIZOU)}{' '}
            </strong>
            {`${laudoMapa.Realizou}`} */}
            <br></br>
            <strong className="mr-auto">
              Médico que Solicitou: {getPrefix(laudoMapa.CODSOLICITANTE)}{' '}
            </strong>
            {`${laudoMapa.NOMEMED}`}
            <hr></hr>
            {urls.length > 0 ? (
              urls.map((url, index) => (
                <div
                  key={index}
                  style={{
                    textAlign: 'center',
                    display: 'block',
                    height: '90vh',
                  }}
                >
                  <iframe
                    src={url?.data?.preSignedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                    title="PDF Viewer"
                  />
                </div>
              ))
            ) : (
              <div className="text-center font-italic">
                Não há imagem para exibir
              </div>
            )}
          </Card.Body>
          <Card.Footer className="text-muted text-center">
            <i className="mr-auto">Conferido e assinado eletronicamente por:</i>
            <strong className="mr-auto"> {getPrefix(laudoMapa.CODREALIZOU)} {laudoMapa.Realizou}</strong>
            <br></br>
            <i className="mr-auto">
              Assinatura Digital: {md5(JSON.stringify(laudoMapa.CODMAPA || ''))}
            </i>
            <div className="mt-2">
              {allowedToDelete(medico, funcionario, 'TODOS') && (
                <Button
                  variant="danger"
                  disabled={files.length === 0}
                  onClick={() => deleteFile(0)}
                >
                  Deletar imagem
                </Button>
              )}
            </div>
          </Card.Footer>
        </>
      )}
    </Card>
  );
}
